/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <ContentWithImage {...cmsData} />)
 * and then make these props fit the final module.
 *
 * Note that propTypes are not needed in this component: the GraphQL schema
 * definition is sufficient for that matter.
 */
import React from "react"
import { graphql } from "gatsby"
import { Section, CmsImage } from "@bw/bits"
import { ContentWithImage as ContentWithImageModule } from "@bw/modules"
// parse is used to ensure that HTML sent by the CMS is converted to React components
import parse from "html-react-parser"

const ContentWithImage = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  idAttribute,
  classAttribute,
  backgroundVariant,
  negativeTextColor,
  imageTakesWholeBackground,
  isFollowedByModuleWithBackgroundVariant,
  text,
  textWidth,
  orientation,
  alignement,
  image,
}) => {
  return (
    <Section
      {...{
        backgroundVariant,
        isFollowedByModuleWithBackgroundVariant,
        idAttribute,
        classAttribute,
        negativeTextColor,
        image: imageTakesWholeBackground ? (
          <CmsImage src={image.backgroundImage} alt={image.alt} />
        ) : null,
        focus: image.has_focus ? image.focus_css : undefined,
      }}
    >
      <ContentWithImageModule
        {...{
          suptitle,
          title,
          subtitle,
          button,
          buttonPosition,
          orientation: orientation.value,
          alignement: alignement.value,
          text: text ? parse(text) : undefined,
          textWidth: textWidth?.value ?? 50,
          image: imageTakesWholeBackground ? null : (
            <CmsImage src={image} alt={image.alt} />
          ),
        }}
      />
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on ContentWithImageModule {
 *   ...ContentWithImageModuleFragment
 * }
 *
 * This is also what you will get in the props of your ContentWithImage
 * resolver component.
 */
export const query = graphql`
  fragment ContentWithImageModuleFragment on ContentWithImageModule {
    image {
      alt
      has_focus
      focus_css
      localImage {
        childImageSharp {
          gatsbyImageData(width: 672)
        }
      }
      backgroundImage: localImage {
        childImageSharp {
          gatsbyImageData(width: 2500)
        }
      }
    }
    orientation {
      value
    }
    alignement {
      value
    }
    text
    textWidth {
      value
    }
    imageTakesWholeBackground
    negativeTextColor
  }
`

export { ContentWithImage }
